<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import Sidebar from 'primevue/sidebar';
import { useCalendarStore } from '@/store';
import { PastDocumentationCard } from './index';
import { Attendee } from '@/types';
import VirtualScroller from 'primevue/virtualscroller';

interface Props {
  open: boolean;
  student?: Attendee;
}

const props = defineProps<Props>();
const calendarStore = useCalendarStore();
const { open, student } = toRefs(props);
const openDialog = ref(false);
const emit = defineEmits(['closePastDocumentations', 'loadStudentPastSessions']);

const studentPastDocumentations = computed(() => {
  const data = calendarStore.pastDocumentations.filter((result) => result.attendee_id === student.value.id);

  if (data.length > 0) {
    calendarStore.updatePastDocumentationState(student.value.id, data[data.length - 1].event.start_at);
  }

  return data;
});

const emitClose = () => {
  emit('closePastDocumentations');
};

watch(open, () => {
  openDialog.value = open.value;
});

const onLazyLoad = (event) => {
  if (event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight) {
    emit('loadStudentPastSessions');
  }
};
</script>

<template>
  <Sidebar
    v-model:visible="openDialog"
    class="past-documentation-sidebar"
    position="right"
    :modal="false"
    @hide="emitClose"
  >
    <h2>
      Past Sessions
      <span>- {{ student?.full_name }}</span>
    </h2>
    <VirtualScroller
      v-if="studentPastDocumentations.length > 0"
      class="custom-loading"
      :items="studentPastDocumentations"
      :item-size="1"
      :delay="250"
      scroll-height="calc(100vh - 53.5px)"
      @scroll="onLazyLoad"
    >
      <template #content="{ items }">
        <PastDocumentationCard v-for="item in items" :key="item.result.id" :data="item" />

        <Skeleton v-if="calendarStore.pastDocumentationsLoading" width="100%" height="1.5rem" />
      </template>
    </VirtualScroller>
    <div v-if="studentPastDocumentations.length == 0" class="bg-white p-3 my-3 border-round-sm text-sm">
      <p>No past sessions to display.</p>
    </div>
  </Sidebar>
</template>

<style scoped lang="scss">
div.p-dialog-content {
  max-height: 500px !important;
}
.dark-medium {
  color: $grayDark;
}
.spacer {
  margin-bottom: 500px;
}
</style>
